'use client';

import { IconButton } from '@magicschool/spellbook/components/IconButton';
import { clearModalReturnTarget, getModalReturnTarget } from '@magicschool/spellbook/components/Modal/modalFocusCoordination';
import {
  ModalCloseSlot,
  ModalContentSlot,
  ModalDescriptionSlot,
  ModalFooterSlot,
  ModalHeaderActionsSlot,
  ModalHeaderAddOnSlot,
  ModalHeaderSlot,
  ModalTooltipContentSlot,
  ModalTooltipSlot,
  ModalTriggerSlot,
} from '@magicschool/spellbook/components/Modal/slots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as ModalPrimitive from 'radix-ui/dialog';
import { useDirection } from 'radix-ui/direction';
import type { ComponentProps, FC } from 'react';
import { useRef } from 'react';

export type ModalProps = ComponentProps<typeof ModalPrimitive.Content> &
  ComponentProps<typeof ModalPrimitive.Root> &
  VariantProps<typeof contentContainerVariants> & {
    /** The label for the close button. */
    closeIconLabel?: string;
    /** If `true`, the modal header will be hidden via the `sr-only` class. */
    hideModalHeader?: boolean;
    /** If `true`, the default close `IconButton` will not display. */
    disableCloseIcon?: boolean;
    /**
     * Optional className applied to the inner content wrapper that groups the header, content, and footer.
     * For `takeover`, defaults to `max-w-[96rem] mx-auto w-full flex flex-col` — centering everything at a
     * consistent max width. Pass `contentClassName` to override (e.g. a narrower reading width).
     */
    contentClassName?: string;
    /**
     * Optional element that wraps the modal's header, content, and footer (e.g. `form`). It renders
     * inside the dialog rather than as its root, so the dialog stays a `div` and keeps a valid role.
     */
    element?: React.ElementType;
    /** If `true`, clicking outside of the modal will not close it. */
    preventOutsideClick?: boolean;
    /**
     * Optional key to coordinate focus restoration for programmatic modal opens.
     * When ControlledModalTrigger is used without this key, focus restoration works
     * automatically. Only needed when programmatically opening modals (e.g., auto-open).
     */
    returnFocusKey?: string;
  };

const overlayVariants = cva(
  [
    'data-[state=open]:animate-in',
    'data-[state=closed]:animate-out',
    'data-[state=closed]:fade-out-0',
    'data-[state=open]:fade-in-0',
    'fixed',
    'inset-0',
    'z-portal',
  ],
  {
    variants: {
      takeover: {
        false: 'bg-surface-backdrop-0 backdrop-blur-sm',
        // Full-viewport surface — no dimmed backdrop or blur since the content covers the entire screen
        true: '',
      },
    },
  },
);

const contentContainerVariants = cva(
  [
    'bg-surface-background-0',
    'data-[state=open]:animate-in',
    'data-[state=closed]:animate-out',
    'data-[state=closed]:fade-out-0',
    'data-[state=open]:fade-in-0',
    'data-[state=closed]:zoom-out-95',
    'data-[state=open]:zoom-in-95',
    'fixed',
    'left-0',
    'top-0',
    'sm:top-[50%]',
    'sm:left-[50%]',
    'sm:translate-x-[-50%]',
    'sm:translate-y-[-50%]',
    'z-portal',
    'flex',
    'flex-col',
    'w-full',
    'h-dvh',
    'sm:h-auto',
    'sm:border',
    'border-border-default',
    'sm:rounded-card-md',
    'sm:shadow-overlay',
    'sm:max-h-[calc(100lvh-8rem)]',
    'duration-200',
  ],
  {
    variants: {
      /** The size of the modal. */
      size: {
        sm: 'sm:max-w-sm',
        lg: 'sm:max-w-3xl',
        xl: 'sm:max-w-5xl',
        // Cancels the sm-breakpoint centering, border/radius, shadow, and max-height applied by the base classes.
        // overflow-y-auto here (not on Modal.Content) gives a page-like scrollbar at the viewport edge.
        // scrollbar-gutter:stable reserves space for the scrollbar so content doesn't shift when it appears.
        takeover:
          'sm:top-0 sm:left-0 sm:translate-x-0 sm:translate-y-0 sm:w-full sm:h-dvh sm:border-0 sm:rounded-none sm:shadow-none sm:max-h-none overflow-y-auto [scrollbar-gutter:stable]',
      },
    },
  },
);

const modalInnerWrapperVariants = cva('', {
  variants: {
    // For takeover: a centered max-width wrapper that aligns header, content, and footer to a shared
    // horizontal axis. The outer overflow-y-auto container handles page-level scrolling.
    // For other sizes: display:contents keeps the element layout-invisible.
    takeover: {
      false: 'contents',
      true: 'max-w-[96rem] mx-auto w-full flex flex-col',
    },
  },
});

const modalCloseVariants = cva('absolute', {
  variants: {
    takeover: {
      false: 'top-2.5 ltr:right-3 rtl:left-3',
      true: 'top-4 ltr:right-4 rtl:left-4',
    },
  },
});

const modalHeaderVariants = cva('flex items-center px-6 py-4 gap-2 flex-wrap rtl:text-right', {
  variants: {
    hasActions: {
      true: 'mb-18 sm:mb-0',
    },
    hasCloseIcon: {
      true: 'max-w-[calc(100%-3rem)]',
    },
    hideModalHeader: {
      true: 'sr-only',
    },
    hasDescription: {
      true: 'pb-1',
    },
  },
});

/**
 * Renders `element` inside the dialog content rather than as its root via `asChild`: a `form`
 * root would carry `role="dialog"`, which axe rejects (`aria-allowed-role`). `display: contents`
 * keeps the content container's flex layout reaching the header, content, and footer, and footer
 * submit buttons stay form descendants.
 */
const ModalElement = ({ children, element: Element, onSubmit }: Pick<ModalProps, 'children' | 'element' | 'onSubmit'>) =>
  Element ? (
    <Element className="contents" onSubmit={onSubmit}>
      {children}
    </Element>
  ) : (
    children
  );

const ModalBase: FC<ModalProps> = ({
  children,
  className,
  closeIconLabel = 'Close',
  contentClassName,
  defaultOpen = false,
  disableCloseIcon = false,
  hideModalHeader = false,
  element,
  modal = true,
  onCloseAutoFocus,
  onOpenChange,
  onOpenAutoFocus,
  onPointerDownOutside,
  onSubmit,
  open,
  preventOutsideClick = false,
  returnFocusKey,
  size = 'sm',
  ...props
}) => {
  const dir = useDirection();
  const overlayRef = useRef<HTMLDivElement>(null);
  const isTakeover = size === 'takeover';

  const handleCloseAutoFocus = (event: Event) => {
    // Try to get return focus target (either by key or from last clicked trigger)
    const returnFocusRef = getModalReturnTarget(returnFocusKey);

    if (returnFocusRef?.current) {
      requestAnimationFrame(() => {
        returnFocusRef.current?.focus();
      });
    } else if (isTakeover) {
      // Let Radix restore focus to the trigger if one was clicked (standard Modal.Trigger).
      // If focus ends up on body afterwards (programmatic open with no trigger), redirect to the
      // main content region. Prefer the app-wide #main-content id (used by SkipToContentLink),
      // falling back to querySelector('main') for pages that haven't adopted it yet.
      requestAnimationFrame(() => {
        if (document.activeElement === document.body || document.activeElement === document.documentElement) {
          (document.getElementById('main-content') ?? document.querySelector<HTMLElement>('main'))?.focus();
        }
      });
    }

    clearModalReturnTarget(returnFocusKey);
    onCloseAutoFocus?.(event);
  };

  const { slots } = useSlots(children, {
    trigger: { Component: ModalTriggerSlot },
    header: { Component: ModalHeaderSlot },
    content: { Component: ModalContentSlot },
    footer: { Component: ModalFooterSlot },
    close: { Component: ModalCloseSlot },
    headerAddOn: { Component: ModalHeaderAddOnSlot },
    headerActions: { Component: ModalHeaderActionsSlot },
    tooltip: { Component: ModalTooltipSlot },
    description: { Component: ModalDescriptionSlot },
  });

  if (!slots.header) console.warn('Modal requires a Header slot.');

  const handlePointerDownOutside = (event: CustomEvent<{ originalEvent: PointerEvent }>) => {
    const target = event.detail.originalEvent.target as HTMLElement;
    const isOutsideOverlay = overlayRef.current && target !== overlayRef.current;
    if (preventOutsideClick || isOutsideOverlay) {
      event.preventDefault();
      return;
    }
    if (onPointerDownOutside) onPointerDownOutside(event);
  };

  return (
    <ModalPrimitive.Root defaultOpen={defaultOpen} modal={modal} onOpenChange={onOpenChange} open={open}>
      {slots.tooltip ?? slots.trigger}
      <ModalPrimitive.Portal data-spellbook-name="modal-portal">
        <ModalPrimitive.Overlay ref={overlayRef} data-spellbook-name="modal-overlay" className={overlayVariants({ takeover: isTakeover })}>
          <ModalPrimitive.Content
            className={cn(contentContainerVariants({ size }), className)}
            data-spellbook-name="modal-content-container"
            dir={dir}
            onCloseAutoFocus={handleCloseAutoFocus}
            onOpenAutoFocus={onOpenAutoFocus}
            onPointerDownOutside={handlePointerDownOutside}
            {...(slots.description ? {} : { 'aria-describedby': undefined })}
            onSubmit={element ? undefined : onSubmit}
            {...props}
          >
            <ModalElement element={element} onSubmit={onSubmit}>
              <div className={cn(modalInnerWrapperVariants({ takeover: isTakeover }), contentClassName)}>
                <div
                  data-spellbook-name="modal-header"
                  className={modalHeaderVariants({
                    hasActions: Boolean(slots.headerActions),
                    hasCloseIcon: Boolean(!disableCloseIcon),
                    hideModalHeader,
                    hasDescription: Boolean(slots.description),
                  })}
                >
                  <ModalPrimitive.Title asChild data-spellbook-name="modal-title">
                    {slots.header}
                  </ModalPrimitive.Title>
                  {slots.headerAddOn}
                </div>
                {slots.description && (
                  <ModalPrimitive.Description asChild className="px-6 pb-4" data-spellbook-name="modal-description">
                    {slots.description}
                  </ModalPrimitive.Description>
                )}
                {slots.content}
                {slots.footer}
                {slots.headerActions}
                {disableCloseIcon ? null : (
                  <ModalPrimitive.Close asChild className={modalCloseVariants({ takeover: isTakeover })} data-spellbook-name="modal-close">
                    <IconButton
                      name="x"
                      size={isTakeover ? 'md' : 'sm'}
                      shape="default"
                      color={isTakeover ? 'primary' : 'neutral'}
                      variant={isTakeover ? 'muted' : 'ghost'}
                      label={closeIconLabel}
                    />
                  </ModalPrimitive.Close>
                )}
              </div>
            </ModalElement>
          </ModalPrimitive.Content>
        </ModalPrimitive.Overlay>
      </ModalPrimitive.Portal>
    </ModalPrimitive.Root>
  );
};

/**
 * A dialog that appears on top of the main content, used for focused user interactions or displaying information without navigating away from the current page.
 *
 * ### When to use
 * - Confirmation dialogs requiring explicit user action (delete, submit, save)
 * - Forms that need focused attention (create, edit, settings)
 * - Detailed content views that shouldn't replace the current page
 * - Workflows requiring a decision before continuing
 *
 * ### When NOT to use
 * - For content that slides in from the edge: use `Drawer` instead
 * - For non-blocking notifications: use `Toast` instead
 * - For simple inline confirmations: use `Alert` instead
 * - For content that works better as a separate page
 *
 * ### API
 * - `size`: Modal presentation — `sm` for simple dialogs, `lg` for complex forms, `xl` for large-scale layouts (1024px max-width, still viewport-padded), `takeover` for full-screen takeover
 * - `open` / `onOpenChange`: Controlled state for programmatic open/close
 * - `defaultOpen`: Uncontrolled initial state
 * - `element`: Wrap the modal's header, content, and footer in a custom element (e.g., `form` for native form submission); the dialog root stays a `div`
 * - `preventOutsideClick`: Disable closing when clicking outside (use sparingly)
 * - `disableCloseIcon`: Hide the default close button — use with `takeover` to force users through a flow
 * - `contentClassName`: Override the inner content wrapper (header + content + footer container). For `takeover`, defaults to `max-w-[96rem] mx-auto w-full flex flex-col`
 *
 * ### Composition (slots)
 * - `<Modal.Trigger>`: Wraps the element that opens the modal. Always include for proper focus management.
 * - `<Modal.Header>`: Required. Renders as `<Text element="h2" variant="heading-3" />`.
 * - `<Modal.Description>`: Optional description below header. Auto-sets `aria-describedby`.
 * - `<Modal.HeaderAddOn>`: Optional content beside header (e.g., badges, status indicators).
 * - `<Modal.Content>`: Main content area with automatic vertical scrolling.
 * - `<Modal.Footer>`: Action buttons. Displays stacked on mobile, row on desktop.
 * - `<Modal.Close>`: Wrap around buttons that should close the modal (e.g., cancel buttons).
 * - `<Modal.HeaderActions>`: Additional header actions for large-format modals (`xl` or `takeover`).
 * - `<Modal.Tooltip>`: Wrap around `Modal.Trigger` to show a tooltip on hover.
 * - `<Modal.TooltipContent>`: Content for the tooltip.
 *
 * ### Visual states
 * - **Opening**: Fade in with zoom animation, backdrop appears
 * - **Closing**: Fade out with zoom animation, backdrop fades
 *
 * ### Accessibility
 * - Focus is trapped within the modal while open
 * - Focus returns to the trigger element when closed; for `takeover` with no trigger, focus falls back to `#main-content` (app-wide convention, used by `SkipToContentLink`) then `<main>`. Ensure that element has `tabIndex={-1}`. For explicit control use `returnFocusKey` with `specifyModalReturnFocus`.
 * - Escape key closes the modal by default — to fully force a flow with `disableCloseIcon`, also pass `onEscapeKeyDown={(e) => e.preventDefault()}`, but only when there is a clear keyboard path forward (e.g. footer buttons)
 * - `aria-labelledby` is automatically set from `<Modal.Header>`
 * - `aria-describedby` is automatically set when `<Modal.Description>` is used
 * - For programmatic opens without a trigger, use `ControlledModalTrigger` or `specifyModalReturnFocus` to ensure focus returns correctly
 *
 * ### Focus management
 * - **Standard pattern**: Use `<Modal.Trigger>` to wrap your button. Focus automatically returns to the trigger on close.
 * - **Controlled pattern**: For multiple triggers opening the same modal, wrap each trigger with `ControlledModalTrigger`. Focus returns to whichever trigger was clicked.
 * - **Programmatic open**: When opening a modal without user interaction (e.g., auto-open), call `specifyModalReturnFocus(key, ref)` before opening to specify where focus should return.
 *
 * @headless Radix Dialog root renders no element; target `modal-trigger` and `modal-content`.
 */
export const Modal = Object.assign(ModalBase, {
  Trigger: ModalTriggerSlot,
  Header: ModalHeaderSlot,
  HeaderAddOn: ModalHeaderAddOnSlot,
  Content: ModalContentSlot,
  Footer: ModalFooterSlot,
  Close: ModalCloseSlot,
  HeaderActions: ModalHeaderActionsSlot,
  Tooltip: ModalTooltipSlot,
  TooltipContent: ModalTooltipContentSlot,
  Description: ModalDescriptionSlot,
});
